/**
 * DispatchBoard — drag-and-drop dispatch board (Wave 3 Part 2 / Quick 260615-uf9).
 *
 * Matches the reference screenshot: a metrics bar, yard/region tabs (ALL + per yard),
 * DAY/NIGHT driver sections each a grid of DriverCards, and a right-side Unassigned
 * accordion grouped by site. Drag a job onto a driver card to assign, onto another
 * card to reassign, onto a sibling job to reorder (insert-before), or onto the
 * Unassigned panel to unassign — all via PATCH /v1/jobs/:id/assignment.
 *
 * Click a job (no drag — 5px activation) opens the job detail modal.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { PanelLeftOpen } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useApiOpts } from '@lodex-pro/web-shared';
import { useJobs, type DispatchJob } from '../jobs/use-jobs';
import { useDrivers, type DriverSummary } from '../jobs/use-drivers';
import { useDriverSyncHealth } from '../drivers/use-driver-sync-health';
import { useYards } from './use-yards';
import { useAssignJob } from './use-assign-job';
import { JobDetailModal } from '../jobs/JobDetailModal';
import { reassignErrorMessage } from '../jobs/driver-lock';
import { DriverCard } from './DriverCard';
import { UnassignedPanel } from './UnassignedPanel';
import {
  unassignedJobs,
  isInProgress,
  isActiveDriverJob,
  driverAvailability,
  formatSyncTooltip,
  isQuietPhone,
  boardVisibleDrivers,
  clampDropToBand,
  sortDriverJobs,
  type DriverAvailability,
  type DriverPresence,
} from './board-utils';
import { useUserTimezone } from '../../lib/use-user-timezone';

const YARD_ALL = '__ALL__';

/**
 * Task #32 — the "Show off-shift drivers" choice, remembered per viewer in this browser.
 * Storage can be unavailable (private mode, blocked site data), so every access is guarded
 * and a failure only means the default: on-shift drivers only.
 */
const SHOW_OFF_SHIFT_KEY = 'lodex.board.showOffShiftDrivers';

function readShowOffShift(): boolean {
  try {
    return window.localStorage.getItem(SHOW_OFF_SHIFT_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeShowOffShift(v: boolean): void {
  try {
    window.localStorage.setItem(SHOW_OFF_SHIFT_KEY, v ? 'true' : 'false');
  } catch {
    // Not remembered this time; the switch still works for this page.
  }
}

/** Task #32 — the board's off-shift switch. The whole control is the ≥56px target. */
function OffShiftSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={(): void => onChange(!checked)}
      style={{
        marginLeft: 'auto',
        minHeight: '56px',
        padding: '0 12px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        border: 'none',
        backgroundColor: 'transparent',
        color: 'var(--color-text-secondary)',
        fontSize: '12px',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      Show off-shift drivers
      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          width: '36px',
          height: '20px',
          flexShrink: 0,
          borderRadius: '10px',
          backgroundColor: checked ? 'var(--color-accent-blue)' : 'var(--color-border-primary)',
          transition: 'background-color 120ms',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: '3px',
            left: checked ? '19px' : '3px',
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: 'var(--color-text-primary)',
            transition: 'left 120ms',
          }}
        />
      </span>
    </button>
  );
}

interface DropTarget {
  driverId: string | null;
  beforeJobId: string | null;
}

function resolveDrop(over: DragEndEvent['over']): DropTarget | null {
  if (over === null) return null;
  const data = over.data.current as
    | { driverId?: string | null; beforeJobId?: string | null }
    | undefined;
  const id = String(over.id);
  if (id.startsWith('jobtarget:')) {
    return { driverId: data?.driverId ?? null, beforeJobId: data?.beforeJobId ?? null };
  }
  if (id.startsWith('driver:')) {
    return { driverId: data?.driverId ?? null, beforeJobId: null };
  }
  if (id === 'unassigned') {
    return { driverId: null, beforeJobId: null };
  }
  return null;
}

export function DispatchBoard(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const jobsQuery = useJobs(apiOpts, {});
  // #4 (260616-d4x) — metric buckets moved to the OperationsPage header bar (visible
  // on both tabs), so the board no longer computes them or fetches completed jobs.
  const { data: driversData } = useDrivers(apiOpts);
  const { data: yardsData } = useYards(apiOpts);
  const { data: syncData } = useDriverSyncHealth();
  const tz = useUserTimezone();
  const assign = useAssignJob();

  const [yardTab, setYardTab] = React.useState<string>(YARD_ALL);
  const [selectedJobId, setSelectedJobId] = React.useState<string | null>(null);
  const [activeJobId, setActiveJobId] = React.useState<string | null>(null);
  // #2 (260616-bd6) — hide the entire Unassigned panel (driver grid goes full width).
  const [panelHidden, setPanelHidden] = React.useState(false);
  // Task #32 — off-shift drivers are hidden unless the viewer asks (remembered per viewer).
  const [showOffShift, setShowOffShift] = React.useState<boolean>(readShowOffShift);
  const onShowOffShift = (v: boolean): void => {
    setShowOffShift(v);
    writeShowOffShift(v);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const jobs = React.useMemo(() => jobsQuery.data?.jobs ?? [], [jobsQuery.data]);
  const drivers = React.useMemo(() => driversData?.drivers ?? [], [driversData]);
  const yards = yardsData?.yards ?? [];

  // Jobs by driver id. Only active-work jobs appear on driver cards — review,
  // billing, and closed statuses are filtered out (field-test fix) so a driver's
  // card reflects what they are actually working right now.
  const jobsByDriver = React.useMemo(() => {
    const map = new Map<string, DispatchJob[]>();
    for (const j of jobs) {
      if (j.driverId === null) continue;
      if (!isActiveDriverJob(j)) continue;
      const arr = map.get(j.driverId) ?? [];
      arr.push(j);
      map.set(j.driverId, arr);
    }
    return map;
  }, [jobs]);

  const unassigned = React.useMemo(() => unassignedJobs(jobs), [jobs]);

  // #3 / OPEN-209 — per-driver availability from the sync-health presence (open shift,
  // or the old 2h last-event rule on an older API) + in-progress jobs.
  const presenceById = React.useMemo(() => {
    const map = new Map<string, DriverPresence>();
    for (const d of syncData?.drivers ?? []) map.set(d.driverId, d);
    return map;
  }, [syncData]);
  const availabilityById = React.useMemo(() => {
    const map = new Map<string, DriverAvailability>();
    for (const d of drivers) {
      const hasActive = (jobsByDriver.get(d.driverId) ?? []).some(isInProgress);
      map.set(d.driverId, driverAvailability(hasActive, presenceById.get(d.driverId) ?? null));
    }
    return map;
  }, [presenceById, drivers, jobsByDriver]);

  // #4 (260616-ggl) / OPEN-210 — per-driver tooltip ("Last synced…" + "Last activity…")
  // and the quiet-phone set (on shift, no phone contact in >30 min).
  const { syncTitleById, quietById } = React.useMemo(() => {
    const titles = new Map<string, string>();
    const quiet = new Set<string>();
    const nowMs = Date.now();
    for (const [id, p] of presenceById) {
      titles.set(id, formatSyncTooltip(p, nowMs, tz));
      if (isQuietPhone(p, nowMs)) quiet.add(id);
    }
    return { syncTitleById: titles, quietById: quiet };
  }, [presenceById, tz]);

  // #4 — quick-assign from the unassigned panel's context menu.
  const onAssign = React.useCallback(
    (jobId: string, driverId: string): void => {
      assign.mutate(
        { jobId, assignedDriverId: driverId, beforeJobId: null },
        { onError: () => toast.error('Could not assign the job. Try again.') },
      );
    },
    [assign],
  );

  // Yard-filtered drivers, then the off-shift filter (task #32), then split by shift.
  const visibleDrivers = React.useMemo(
    () =>
      boardVisibleDrivers(
        yardTab === YARD_ALL ? drivers : drivers.filter((d) => d.homeYardId === yardTab),
        jobsByDriver,
        showOffShift,
      ),
    [drivers, yardTab, jobsByDriver, showOffShift],
  );
  const dayDrivers = visibleDrivers.filter((d) => d.shift !== 'NIGHT');
  const nightDrivers = visibleDrivers.filter((d) => d.shift === 'NIGHT');

  const activeJob = activeJobId !== null ? (jobs.find((j) => j.id === activeJobId) ?? null) : null;

  const onDragStart = (e: DragStartEvent): void => {
    const data = e.active.data.current as { jobId?: string } | undefined;
    setActiveJobId(data?.jobId ?? null);
  };

  const onDragEnd = (e: DragEndEvent): void => {
    setActiveJobId(null);
    const activeData = e.active.data.current as
      | { jobId?: string; driverId?: string | null }
      | undefined;
    const jobId = activeData?.jobId;
    if (jobId === undefined) return;
    const target = resolveDrop(e.over);
    if (target === null) return;
    // No-op: dropped onto itself.
    if (target.beforeJobId === jobId) return;
    // No-op: dropped onto its own driver card with no reorder anchor and already there.
    if (target.driverId === (activeData?.driverId ?? null) && target.beforeJobId === null) return;
    // #46 / DEC-361 — in a driver column a drag stays within the job's band (going,
    // urgent, the rest): a drop past the band edge lands on the edge.
    const moved = jobs.find((j) => j.id === jobId);
    const beforeJobId =
      target.driverId === null || moved === undefined
        ? target.beforeJobId
        : clampDropToBand(
            sortDriverJobs(jobsByDriver.get(target.driverId) ?? []),
            moved,
            target.beforeJobId,
          );
    if (beforeJobId === jobId) return;
    assign.mutate(
      { jobId, assignedDriverId: target.driverId, beforeJobId },
      {
        // OPEN-157 — a refused move (a run ticket landed since the board loaded) says why.
        onError: (err: unknown) =>
          toast.error(reassignErrorMessage(err, 'Could not move the job. Try again.')),
      },
    );
  };

  if (jobsQuery.isLoading) {
    return <p style={{ padding: '16px', fontSize: '14px' }}>Loading board…</p>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div
        style={{
          display: 'flex',
          gap: '12px',
          padding: '12px',
          height: '100%',
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        {/* Left: yard tabs + driver grid (metrics moved to the header bar — #4) */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            minWidth: 0,
            overflowY: 'auto',
          }}
        >
          {/* Yard tabs */}
          <div
            style={{
              display: 'flex',
              gap: '4px',
              flexWrap: 'wrap',
              borderBottom: '1px solid var(--color-border-secondary)',
            }}
          >
            <YardTab
              label="ALL"
              active={yardTab === YARD_ALL}
              onClick={(): void => setYardTab(YARD_ALL)}
            />
            {yards.map((y) => (
              <YardTab
                key={y.id}
                label={y.name.toUpperCase()}
                active={yardTab === y.id}
                onClick={(): void => setYardTab(y.id)}
              />
            ))}
            <OffShiftSwitch checked={showOffShift} onChange={onShowOffShift} />
          </div>

          <ShiftSection
            title="Day Drivers"
            drivers={dayDrivers}
            jobsByDriver={jobsByDriver}
            availabilityById={availabilityById}
            syncTitleById={syncTitleById}
            quietById={quietById}
            onSelect={setSelectedJobId}
          />
          <ShiftSection
            title="Night Drivers"
            drivers={nightDrivers}
            jobsByDriver={jobsByDriver}
            availabilityById={availabilityById}
            syncTitleById={syncTitleById}
            quietById={quietById}
            onSelect={setSelectedJobId}
          />
        </div>

        {/* Right: unassigned accordion (CSS-hidden when collapsed — stays mounted) */}
        <UnassignedPanel
          jobs={unassigned}
          drivers={drivers}
          availabilityById={availabilityById}
          quietById={quietById}
          onSelect={setSelectedJobId}
          onAssign={onAssign}
          hidden={panelHidden}
          onHide={(): void => setPanelHidden(true)}
        />

        {/* #2 — floating "show" button when the panel is hidden. */}
        {panelHidden && (
          <button
            type="button"
            onClick={(): void => setPanelHidden(false)}
            aria-label="Show unassigned panel"
            title="Show unassigned panel"
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              zIndex: 6,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-primary)',
              backgroundColor: 'var(--color-bg-secondary)',
              color: 'var(--color-text-secondary)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
            }}
          >
            <PanelLeftOpen size={15} />
            Unassigned
            <span
              style={{
                minWidth: '18px',
                height: '18px',
                padding: '0 5px',
                borderRadius: '9px',
                backgroundColor: 'var(--color-warning)',
                color: 'var(--color-on-accent)',
                fontSize: '11px',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {unassigned.length}
            </span>
          </button>
        )}

        {/* #5 — refresh indicator (bottom-right): "Refreshing…" during the 30s
            jobs poll, else green dot + "Updated HH:MM:SS" (user-local time). */}
        <RefreshIndicator
          isFetching={jobsQuery.isFetching}
          updatedAtMs={jobsQuery.dataUpdatedAt}
          tz={tz}
        />
      </div>

      {/* dropAnimation={null} — Quick 260616-fxg #3: no fly-back-to-origin animation;
          the optimistic cache update already places the job in its new spot. */}
      <DragOverlay dropAnimation={null}>
        {activeJob !== null ? (
          <div
            style={{
              padding: '8px 10px',
              borderRadius: '6px',
              border: '1px solid var(--color-accent-blue)',
              backgroundColor: 'var(--color-bg-tertiary)',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            {activeJob.haulId ?? '—'} · {activeJob.siteName}
          </div>
        ) : null}
      </DragOverlay>

      <JobDetailModal jobId={selectedJobId} onClose={(): void => setSelectedJobId(null)} />
    </DndContext>
  );
}

/**
 * #5 (260616-bd6) — poll freshness indicator pinned bottom-right of the board.
 * Mirrors the Map View pattern: "Refreshing…" while the 30s jobs poll is in
 * flight, otherwise a green dot + "Updated HH:MM:SS" (last successful fetch,
 * rendered in the user's timezone — never UTC, DEC-004).
 */
function RefreshIndicator({
  isFetching,
  updatedAtMs,
  tz,
}: {
  isFetching: boolean;
  updatedAtMs: number;
  tz: string;
}): React.JSX.Element {
  const updatedLabel = React.useMemo(() => {
    if (updatedAtMs === 0) return null;
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(new Date(updatedAtMs));
  }, [updatedAtMs, tz]);

  return (
    <div
      aria-live="polite"
      style={{
        position: 'absolute',
        bottom: '14px',
        right: '16px',
        zIndex: 5,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 9px',
        borderRadius: '6px',
        backgroundColor: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border-primary)',
        fontSize: '12px',
        color: 'var(--color-text-secondary)',
        pointerEvents: 'none',
        boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
      }}
    >
      {isFetching ? (
        'Refreshing…'
      ) : (
        <>
          <span
            aria-hidden="true"
            style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-success)',
              flexShrink: 0,
            }}
          />
          {updatedLabel === null ? 'Updated' : `Updated ${updatedLabel}`}
        </>
      )}
    </div>
  );
}

function YardTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.03em',
        cursor: 'pointer',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-accent-blue)' : '2px solid transparent',
        backgroundColor: 'transparent',
        color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
      }}
    >
      {label}
    </button>
  );
}

function ShiftSection({
  title,
  drivers,
  jobsByDriver,
  availabilityById,
  syncTitleById,
  quietById,
  onSelect,
}: {
  title: string;
  drivers: readonly DriverSummary[];
  jobsByDriver: Map<string, DispatchJob[]>;
  availabilityById: ReadonlyMap<string, DriverAvailability>;
  syncTitleById: ReadonlyMap<string, string>;
  quietById: ReadonlySet<string>;
  onSelect: (jobId: string) => void;
}): React.JSX.Element {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <h3
        style={{
          fontSize: '12px',
          fontWeight: 800,
          color: 'var(--color-text-tertiary)',
        }}
      >
        {title} ({drivers.length})
      </h3>
      {drivers.length === 0 ? (
        <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>No drivers.</span>
      ) : (
        <div
          style={{
            display: 'grid',
            // #1 (260616-d4x) — narrower cards (240 vs 300) → more per row. Long
            // job-chip site names truncate with ellipsis + hover tooltip (JobChip).
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '10px',
          }}
        >
          {drivers.map((d) => (
            <DriverCard
              key={d.driverId}
              driver={d}
              jobs={jobsByDriver.get(d.driverId) ?? []}
              availability={availabilityById.get(d.driverId) ?? 'available'}
              syncTitle={syncTitleById.get(d.driverId)}
              quiet={quietById.has(d.driverId)}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </section>
  );
}
